import React, { HTMLAttributes, useMemo } from 'react';
import Immutable from 'immutable';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import c from 'classnames';

import {
  Paragraph,
} from '@uncinc/react-drupal-core';
import { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import { useHistory, useLocation } from 'react-router';
import {
  BlockCta,
  BlockProgramCompleted,
  BlockStreak,
  Button,
  Main,
  RecoveryPlanWeekOverview,
} from '../../../components';
import useClasses from '../../../hooks/useClasses';
import useRoutes from '../../../hooks/useRoutes';
import { ImmutableProgramNodeEntity } from '..';
import useDailyRegistrations from '../../../hooks/useDailyRegistrations';
import useWindowDimensions from '../../../hooks/useWindowDimensions';
import useScrollDirection from '../../../hooks/useScrollDirection';
import useProgramCompletion from '../../../hooks/useProgramCompletion';

import {
  DOMAIN_ZELFHULP_NAASTEN,
  ROUTE_LOGIN_SEARCH_PARAM,
  SCREEN_NORMAL,
  SCREEN_TABLET,
  SCROLL_OFFSET_AVAILABLE_EXERCISE,
  SCROLL_OFFSET_LOCKED_EXERCISE,
  SCROLL_POSITION_THRESHOLD,
  SCROLL_OFFSET_EXERCISE_EXTRA_MOBILE,
  DOMAIN_MENTAAL_BALANS,
} from '../../../config/constants';
import { RegistrationsState } from '../../../types';

import './style.scss';

interface Props extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  entity: ImmutableProgramNodeEntity;
}

interface Submissions {
  [key: string]: Object[]
}

const NodeProgram: React.FC<Props> = ({ entity, className }) => {
  const { width } = useWindowDimensions();
  const { buildClass, joinClasses } = useClasses('NodeProgram');
  const { t } = useTranslation();
  const {
    registrationsForCurrentWeek,
    currentWeekNumber,
    getHighestAmountOrGoal,
  } = useDailyRegistrations();

  const isInitialRegistrationsLoaded = useSelector<RegistrationsState, boolean>(
    (state) => state.getIn(['registrations', 'isInitialLoaded'], false),
  );

  const outstandingRegistrations = useSelector<RegistrationsState, Immutable.List<string>>(
    (state) => state.getIn(['registrations', 'outstandingRegistrations'], Immutable.OrderedSet()),
  ).toList();

  const isRecoveryPlanSet = useSelector<RegistrationsState, boolean>(
    (state) => state.getIn(['registrations', 'isRecoveryPlanSet'], false),
  );

  const userId = useSelector<ImmutableRootState<number>, number>((state) => state.getIn(['login', 'uid'], null));
  const startDate = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['user', userId, 'field_program_start_date', 0, 'value'], null));
  const completedExercises = useSelector<ImmutableRootState<Submissions>, Submissions>((state) => state.getIn(['exerciseSubmissions', 'submissions'], {}));
  // field phase day interval is 1-indexed
  const fieldPhaseDayInterval = entity.getIn(['field_phase_day_interval', 0, 'value'], 1);
  const phaseUnlockInfo = entity.getIn(['field_phase_unlock_info', 0, 'value'], '');

  const today = new Date();
  const routes = useRoutes();
  const location = useLocation();
  const history = useHistory();

  const {
    scrollPosition,
  } = useScrollDirection({
    threshold: SCROLL_POSITION_THRESHOLD as number,
  });

  const hasReachedTop = useMemo(() => (
    scrollPosition <= SCROLL_POSITION_THRESHOLD
  ), [scrollPosition]);
  const blockCtaClassNames = c({
    'NodeProgram__BlockCta--is-moving': !hasReachedTop,
  });

  const isSmallerThanTablet = useMemo(() => (
    width < SCREEN_TABLET
  ), [width]);

  // For scrolling to the current exercise.
  React.useEffect(() => {
    setTimeout(() => {
      // Do not scroll when there are no exercises completed yet.
      if (!document.querySelector('.ParagraphExercise--lastExerciseDone') &&
        !document.querySelector('.ParagraphExercise--done')) {
        return;
      }
      let currentExercise = document.querySelector('.ParagraphExercise--available');
      let scrollOffset = currentExercise ? SCROLL_OFFSET_AVAILABLE_EXERCISE as number
        : SCROLL_OFFSET_LOCKED_EXERCISE as number;
      if (isSmallerThanTablet) {
        scrollOffset += SCROLL_OFFSET_EXERCISE_EXTRA_MOBILE;
      }
      // If no exercise is "available", scroll to the first locked exercise.
      if (!currentExercise) {
        currentExercise = document.querySelector('.ParagraphExercise--locked');
      }
      if (currentExercise) {
        const exercisePosition = currentExercise.getBoundingClientRect().top;
        const position = window.scrollY + exercisePosition - scrollOffset;
        window.scrollTo({ top: position, behavior: 'smooth' });
        currentExercise.querySelector('a')?.focus({ preventScroll: true });
      }
    }, 500); // Wait a bit for the classnames of exercises to be set.
  }, [isSmallerThanTablet]);

  React.useEffect(() => {
    if (!location.search.includes(ROUTE_LOGIN_SEARCH_PARAM)) {
      return;
    }

    if (isRecoveryPlanSet && outstandingRegistrations.size > 0) {
      const latestOutstandingRegistration = outstandingRegistrations
        .get(outstandingRegistrations.size - 1);

      history.push(`${routes.dailyRegistration}?date=${latestOutstandingRegistration}`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecoveryPlanSet, outstandingRegistrations]);

  const currentDomain = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['uiInternal', 'currentDomain', 'raw'], ''));

  const phases = entity.get('field_paragraphs', Immutable.List());

  const { isProgramCompleted } = useProgramCompletion({ phases });

  let currentPhase: number = 0;

  // According to the theory, phases are unlocked IF the phase day interval has passed
  // relative to the program's start date AND the last exercise of the previous phase is completed.
  phases.forEach((_, index) => {
    const daysUntilUnlocked = index * fieldPhaseDayInterval;
    const unlockDate = new Date(startDate);
    unlockDate.setDate(unlockDate.getDate() + daysUntilUnlocked);
    unlockDate.setHours(0, 0, 0, 0);
    let isLastExerciseOfPreviousPhaseCompleted = false;

    // mark the exercises of the previous phase as true
    // if we loop through the first phase, because there
    // is no previous phase
    if (index === 0) {
      isLastExerciseOfPreviousPhaseCompleted = true;
    } else {
      const previousPhase = phases.get(index - 1);
      const exercisesOfPreviousPhase = previousPhase.getIn(['entity', 'field_exercise'], Immutable.List());
      const lastExerciseInPreviousPhase = !exercisesOfPreviousPhase.isEmpty()
        ? exercisesOfPreviousPhase.last()
        : Immutable.Map();
      const lastExerciseWebformId = lastExerciseInPreviousPhase.getIn(['entity', 'field_webform', 0, 'target_id'], null);
      isLastExerciseOfPreviousPhaseCompleted = lastExerciseWebformId
        ? !!completedExercises[lastExerciseWebformId]
        : false;
    }

    if (
      startDate
      && unlockDate.getTime() <= today.getTime()
      && isLastExerciseOfPreviousPhaseCompleted
    ) {
      currentPhase = index;
    }
  });

  const endOfProgramExercises = entity.get('field_end_program_exercises', Immutable.List())
    .map((exercise) => {
      const title = exercise.getIn(['entity', 'field_title', 0, 'value'], '');
      const iconType = exercise.getIn(['entity', 'field_icon', 0, 'value'], '');
      const url = exercise.getIn(['entity', 'field_webform', 0, 'url'], '');
      const webformId = exercise.getIn(['entity', 'field_webform', 0, 'target_id']);
      const done = !!completedExercises[webformId];

      return {
        title,
        iconType,
        url,
        done,
      };
    });

  const shouldShowAside = useMemo(() => (
    currentDomain !== DOMAIN_ZELFHULP_NAASTEN && currentDomain !== DOMAIN_MENTAAL_BALANS
  ), [currentDomain]);

  return (
    <>
      {shouldShowAside && isSmallerThanTablet && (
        <div className="sidebar-wrapper">
          <aside className={buildClass('recovery-plan-week-overview')}>
            <BlockCta
              className={joinClasses(buildClass('BlockCta'), blockCtaClassNames)}
            />
          </aside>
        </div>
      )}
      <Main className="col-md-7 col-lg-6 py-md-8 py-lg-12">
        <h1 className="visually-hidden">{t('pages.account.settings.program.title')}</h1>
        <BlockProgramCompleted
          heading={entity.getIn(['field_end_program_title', 0, 'value'], '')}
          text={entity.getIn(['field_end_program_text', 0, 'value'], '')}
          exercises={endOfProgramExercises.toJS()}
          image={entity.getIn(['field_end_program_image', 0, 'entity', 'field_media_file', 0, 'url'], '')}
          completed={isProgramCompleted}
        />
        <div className={joinClasses(className, buildClass())}>
          {phases.size > 0 && (
            <div>
              {phases.map((paragraph, index) => {
                const id = paragraph.get('target_id');

                const daysUntilUnlocked = index * fieldPhaseDayInterval;
                const unlockDate = new Date(startDate);
                unlockDate.setDate(unlockDate.getDate() + daysUntilUnlocked);
                unlockDate.setHours(0, 0, 0, 0);

                return (
                  <Paragraph
                    key={`paragraph:${id}`}
                    id={id}
                    entity={paragraph.get('entity')}
                    phaseIndex={index}
                    currentActivePhaseIndex={currentPhase}
                    phaseIsActive={index === currentPhase}
                    unlockDate={unlockDate}
                    unlockInfo={phaseUnlockInfo}
                  />
                );
              })}
            </div>
          )}
        </div>
      </Main>
      {shouldShowAside && !isSmallerThanTablet && (
      <div className="col-md-5 col-lg-4 py-md-8 py-lg-12">
        <aside className={buildClass('recovery-plan-week-overview')}>
          <BlockCta className="mt-0 mb-4" />
          {width >= SCREEN_NORMAL && (
            <>
              <BlockStreak />
              {registrationsForCurrentWeek && (
              <RecoveryPlanWeekOverview
                isLoading={!isInitialRegistrationsLoaded}
                currentWeekNumber={currentWeekNumber}
                registrations={registrationsForCurrentWeek}
                highestAmount={getHighestAmountOrGoal(registrationsForCurrentWeek)}
                hideGoalMessage
              />
              )}
              <Button secondary href={routes.recoveryPlan}>
                {t('pages.program.buttons.view_entire_plan')}
              </Button>
            </>
          )}
        </aside>
      </div>
      )}
    </>
  );
};

export default NodeProgram;
